/**
 * =========================================================================================
 * Component - Main (Component - App)
 * =========================================================================================
 **/

import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from "react-redux";
import * as Icon from 'ubs-icons';
import UToast from "ubs-framework/v2/UToast";
import UForceLogout from "ubs-framework/v2/UForceLogout";
import {checkCookie, createCookie, eraseCookie, getCookie} from "ubs-framework/v2/common/function";
import {onLogout, sendData, socket, socketConnect} from "./store/actions";

const MAX_MASTER_FETCH_ATTEMPTS = 3;
// Mirrors the server-side cap on `ats|master|candidateList`.
const CANDIDATE_RESOLVE_BATCH_LIMIT = 100;
import AppATS from "./app-ats";
import AppATSPublic from "./app-ats/public";
import ATSHistory from "./app-ats/ats-history";
import {detectDevice} from 'ubs-framework/v2/common/function';
import {IS_DEV} from "./common/constant";
import PushNotification from "./app-ats/common/push-notification";
import {useHistory, useRouteMatch} from "react-router-dom";
import UErrorPage from "ubs-framework/v2/UErrorPages/UErrorPage";
import {UFrameworkProvider} from "ubs-framework/v2/context"
import useSocket from './app-ats/common/hooks/useSocket';

const publicPath = ['/job', '/interview','/:orgId/verification' , "/sign" , "/interview-form", '/evaluation']

const App = () => {
    let matchPath = useRouteMatch(publicPath)?.path;
    const DISPATCH = useDispatch();
    const history = useHistory();
    const _UAC_SLICE = useSelector(e => e?.ats?.uac);
    const _UAC = useSelector(e => !!e?.ats?.uac);
    const _UT = useSelector(e => e?.ats?.userType);
    const _SOCKET_ID = useSelector(e => e.common.socketId);
    const _ID = useSelector(e => e?.common?._id);
    const _USERS = useSelector(e => e?.ats?.userObj || {});
    const _ALO = useSelector(e => e?.ats?.accessLevelObj || {});
    const _FU_IDL = useSelector(e => e?.ats?.findUserIdsList || []);
    const _FAL_IDL = useSelector(e => e?.ats?.findAccessLevelIdsList || []);
    const _P_BAR = useSelector(e => e?.ats?.paymentbar);
    const _ERROR = useSelector(e => e?.common?.error)
    const _S3_BASE_URL = useSelector(e => e.common.S3_BASE_URL);
    const _CANDIDATES = useSelector(e => e?.ats?.candidateObj || {});
    const _FC_IDL = useSelector(e => e?.ats?.findCandidateIdsList || []);

    /**
     * Per-id master-fetch attempt counters for this session (id -> attempts).
     * Bounds an infinite request loop without blacklisting: an id that never resolves into
     * its cache (deleted, outside the caller's visibility, or re-dispatched by a render loop)
     * is fetched at most MAX_MASTER_FETCH_ATTEMPTS times, then ignored. A valid id resolves on
     * the first response and then lives in the cache, so the cap is never reached.
     * Candidates need this more than users do: `atsVilibility` hides candidates outside a
     * restricted recruiter's scope, so 'requested id never comes back' is ordinary here.
     */
    const userFetchAttempts = useRef(new Map());
    const accessLevelFetchAttempts = useRef(new Map());
    const candidateFetchAttempts = useRef(new Map());

    const uac = {
        primaryUac: _UAC_SLICE,
    };

    useEffect(() => {
        if (!publicPath.includes(matchPath)) {

            if (checkCookie('ubs_token')) {
                /** Clear URL and connect socket */
                eraseCookie('previousURL');
                socketConnect(DISPATCH, history, flag => {
                    if (flag) {
                        const orgId = window.location.pathname.split('/')[1];
                        let data = {};
                        data.platform = detectDevice(true)
                        if (orgId.length === 10) data = {...data, token: getCookie('ubs_token'), orgId};
                        else data = {...data, token: getCookie('ubs_token')};
                        const isPreBoardingRoute = window.location.pathname.split('/')?.[2] === 'boarding-document'
                        if (getCookie('ubs_token')) {
                            if (isPreBoardingRoute) {
                                sendData('onboardingAuth', data);
                            }else sendData('authenticate', data);
                        }
                    } else {
                        /**
                         * Client server socket connection fail so
                         * TODO: Refresh popup code Goes here
                         */
                    }
                });
            } else {
                /**
                 * Store current URL in cookie and redirect to account module
                 */
                if (window.location.pathname !== '/logout') createCookie('previousURL', window.location.href);
                window.location.href = process.env.REACT_APP_ACCOUNT_MODULE_URL;
            }
        }
        if (IS_DEV) {
            document.body.classList.add('development')
        }
    }, [matchPath]);

    useEffect(() => {
        if (!publicPath.includes(matchPath)) {
            {/** User Info Find & Access Level Fined Logic **/
            }
            const userIds = new Set();
            const accessLevelIds = new Set();

            const candidateIds = new Set();

            /**
             * Only count an attempt when the request can actually be emitted - `sendData` no-ops
             * until the socket is connected and authenticated, and counting an un-emitted request
             * would burn the retry budget and blank valid rows during the reconnect window.
             */
            const canEmit = () => socket?.connected && socket?.isAuthenticated;

            // check user ids
            if (_FU_IDL?.length !== 0) {
                _FU_IDL.map(e => {
                    const id = e?._id || e;
                    if (id && !_USERS?.[id] && (userFetchAttempts.current.get(id) || 0) < MAX_MASTER_FETCH_ATTEMPTS) userIds.add(id)
                })
                DISPATCH({type: 'FIND_MASTER_DATA_LIST', payload: {findUserIdsList: []}})
                if (userIds.size) {
                    if (canEmit()) userIds.forEach(id => userFetchAttempts.current.set(id, (userFetchAttempts.current.get(id) || 0) + 1))
                    sendData('hrms|master|get', {userIds: [...userIds]})
                }
            }

            // check access level ids
            if (_FAL_IDL?.length !== 0) {
                _FAL_IDL.map(e => {
                    const id = e?._id || e;
                    if (id && !_ALO?.[id] && (accessLevelFetchAttempts.current.get(id) || 0) < MAX_MASTER_FETCH_ATTEMPTS) accessLevelIds.add(id)
                })
                DISPATCH({type: 'FIND_MASTER_DATA_LIST', payload: {findAccessLevelIdsList: []}})
                if (accessLevelIds.size) {
                    if (canEmit()) accessLevelIds.forEach(id => accessLevelFetchAttempts.current.set(id, (accessLevelFetchAttempts.current.get(id) || 0) + 1))
                    sendData('hrms|master|get', {accessLevelIds: [...accessLevelIds]})
                }
            }

            // check candidate ids - resolves photo/name for screens that never query the
            // candidate collection themselves (notifications and anything holding only ids).
            if (_FC_IDL?.length !== 0) {
                _FC_IDL.map(e => {
                    const id = e?._id || e;
                    if (id && !_CANDIDATES?.[id] && (candidateFetchAttempts.current.get(id) || 0) < MAX_MASTER_FETCH_ATTEMPTS) candidateIds.add(id)
                })
                DISPATCH({type: 'FIND_MASTER_DATA_LIST', payload: {findCandidateIdsList: []}})
                if (candidateIds.size) {
                    if (canEmit()) candidateIds.forEach(id => candidateFetchAttempts.current.set(id, (candidateFetchAttempts.current.get(id) || 0) + 1))
                    // Chunked to the event's server-side cap so a large list cannot be rejected.
                    const ids = [...candidateIds];
                    for (let i = 0; i < ids.length; i += CANDIDATE_RESOLVE_BATCH_LIMIT) {
                        sendData('ats|master|candidateList', {candidateIds: ids.slice(i, i + CANDIDATE_RESOLVE_BATCH_LIMIT)})
                    }
                }
            }
        }
    }, [matchPath, _FU_IDL, _FAL_IDL, _FC_IDL])

    /**
     * A new socketId means a fresh (re)connection - reset the per-id fetch-attempt budget so any
     * id that hit the cap during a transient drop is retried on the new socket. Without this an
     * employee/candidate whose lookup failed three times stays blank for the rest of the session.
     * Within a single connection the cap still bounds runaway loops. Mirrors frontend/hrms/src/App.jsx.
     */
    useEffect(() => {
        userFetchAttempts.current.clear();
        accessLevelFetchAttempts.current.clear();
        candidateFetchAttempts.current.clear();
    }, [_SOCKET_ID])

    return (
        <UFrameworkProvider value={{ S3_BASE_URL : _S3_BASE_URL, sendData, useSocket, ID: _ID, SOCKET_ID: _SOCKET_ID, ISA: _UT === 1, uac }} autoFetch={['customFields']}>
            <div className={`u_h-full u_ats ${_P_BAR?.isPaymentbar ? 'u_paymentbar' : ''}`}>
                {!!_ERROR && <UErrorPage title={_ERROR?.title} description={_ERROR?.description} isRedirectAccount={_ERROR?.isRedirectAccount} module="recruit" onLogout={onLogout}/>}
                {publicPath.includes(matchPath) ? <React.Fragment>
                        <AppATSPublic/>
                    </React.Fragment> :
                    <React.Fragment>
                        {_UAC ?
                            <React.Fragment>
                                <PushNotification/>
                                <AppATS/>
                            </React.Fragment> :
                            <div className="u_preloader">
                                <div className="u_m-auto u_t-center">
                                    <Icon.Preloader/>
                                    <p>{!_SOCKET_ID ? 'Connecting' : 'Retrieving'}...</p>
                                </div>
                            </div>
                        }
                    </React.Fragment>}
                {_SOCKET_ID?.length !== 0 && <ATSHistory/>}
                <UToast/>
                <UForceLogout/>
                {/** Do not remove below p tag :
                 Usage: IT is using in copy task link
                 */}
                <p style={{fontSize: '0rem', height: 0}} id="u_copy-task-link"/>
            </div>
        </UFrameworkProvider>
    );
};

export default App;

